"use client";

import React, { useEffect, useRef, useState } from "react";
import { Zap, ShieldCheck, Users, Globe, Cpu } from "lucide-react";
import { Button } from "@/components/ui/button";
export default function FutureProofingSmbs() {
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const containerRef = useRef<HTMLElement>(null);
  const [mousePos, setMousePos] = useState({
    x: 0,
    y: 0
  });
  const [isVisible, setIsVisible] = useState(false);
  const stats = [{
    id: 1,
    label: "1GW Pipeline",
    icon: <Zap className="size-6" />
  }, {
    id: 2,
    label: "120 MW Under Site",
    icon: <ShieldCheck className="size-6" />
  }, {
    id: 3,
    label: "+55 Yrs Experience",
    icon: <Users className="size-6" />
  }, {
    id: 4,
    label: "Global Infrastructure",
    icon: <Globe className="size-6" />
  }];
  useEffect(() => {
    const timer = setTimeout(() => {
      setIsVisible(true);
    }, 150);
    return () => clearTimeout(timer);
  }, []);
  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext("2d");
    if (!ctx) return;
    let width = canvas.width = window.innerWidth;
    let height = canvas.height = window.innerHeight;
    const particles: {
      x: number;
      y: number;
      vx: number;
      vy: number;
    }[] = [];
    const particleCount = Math.min(80, width * height / 20000);
    const connectionDistance = 180;
    const mouseDistance = 250;
    for (let i = 0; i < particleCount; i++) {
      particles.push({
        x: Math.random() * width,
        y: Math.random() * height,
        vx: (Math.random() - 0.5) * 0.3,
        vy: (Math.random() - 0.5) * 0.3
      });
    }
    const animate = () => {
      ctx.clearRect(0, 0, width, height);
      particles.forEach((p, i) => {
        p.x += p.vx;
        p.y += p.vy;
        if (p.x < 0 || p.x > width) p.vx *= -1;
        if (p.y < 0 || p.y > height) p.vy *= -1;
        ctx.beginPath();
        ctx.arc(p.x, p.y, 1.5, 0, Math.PI * 2);
        ctx.fillStyle = "rgba(28, 25, 23, 0.1)";
        ctx.fill();
        for (let j = i + 1; j < particles.length; j++) {
          const p2 = particles[j];
          const dx = p.x - p2.x;
          const dy = p.y - p2.y;
          const dist = Math.sqrt(dx * dx + dy * dy);
          if (dist < connectionDistance) {
            ctx.beginPath();
            ctx.strokeStyle = `rgba(180, 83, 9, ${0.1 * (1 - dist / connectionDistance)})`;
            ctx.lineWidth = 0.5;
            ctx.moveTo(p.x, p.y);
            ctx.lineTo(p2.x, p2.y);
            ctx.stroke();
          }
        }
        const dx = p.x - mousePos.x;
        const dy = p.y - mousePos.y;
        const dist = Math.sqrt(dx * dx + dy * dy);
        if (dist < mouseDistance) {
          ctx.beginPath();
          ctx.strokeStyle = `rgba(180, 83, 9, ${0.15 * (1 - dist / mouseDistance)})`;
          ctx.moveTo(p.x, p.y);
          ctx.lineTo(mousePos.x, mousePos.y);
          ctx.stroke();
          p.x -= dx * 0.0002;
          p.y -= dy * 0.0002;
        }
      });
      requestAnimationFrame(animate);
    };
    const handleResize = () => {
      width = canvas.width = window.innerWidth;
      height = canvas.height = window.innerHeight;
    };
    window.addEventListener("resize", handleResize);
    const animationId = requestAnimationFrame(animate);
    return () => {
      window.removeEventListener("resize", handleResize);
      cancelAnimationFrame(animationId);
    };
  }, [mousePos]);
  const handleMouseMove = (e: React.MouseEvent) => {
    const rect = containerRef.current?.getBoundingClientRect();
    if (rect) {
      setMousePos({
        x: e.clientX - rect.left,
        y: e.clientY - rect.top
      });
    }
  };
  return <section data-section-id="17074" id="the-neural-core" ref={containerRef} onMouseMove={handleMouseMove} className="relative min-h-screen w-full flex items-start justify-center overflow-hidden bg-[#FDFAF3] pt-40 md:pt-48 pb-24 md:pb-32 px-4 md:px-8">
      <canvas ref={canvasRef} className="absolute inset-0 z-0 pointer-events-none opacity-60" />

      <div className={`relative z-10 w-full max-w-5xl mx-auto flex flex-col items-center text-center transition-all duration-1000 ease-out ${isVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-10"}`}>
        <div className="inline-flex items-center gap-2 px-4 py-1.5 rounded-lg border border-[#1C1917]/10 bg-[#F5EDD8] text-[#B45309] text-xs font-bold tracking-widest uppercase mb-10">
          <Cpu className="size-4" />
          <span>Institutional AI Infrastructure Capital</span>
        </div>

        <h1 className="text-4xl md:text-6xl lg:text-7xl font-sans font-semibold tracking-tight leading-[1.1] text-[#1C1917] mb-12">
          Deploying Capital Into the{" "}
          <span className="text-[#B45309]">Backbone</span>
          <br />
          of Artificial Intelligence
        </h1>

        <p className="text-lg md:text-xl text-[#1C1917]/70 max-w-3xl mx-auto leading-relaxed font-sans mb-24">
          N2N Capital is an infrastructure investment firm building a phased 1GW
          AI-ready data center platform across the United States, India, and
          Mexico. We acquire and develop power-secured, compute-ready assets in
          AI-undersupplied markets.
        </p>

        <div className="grid grid-cols-2 md:grid-cols-4 gap-8 md:gap-16 w-full">
          {stats.map((stat, i) => <div key={stat.id} data-index={i} className="flex flex-col items-center gap-4 group">
              <div className="p-3 rounded-xl bg-[#F5EDD8] border border-[#1C1917]/10 text-[#1C1917] group-hover:text-[#B45309] group-hover:border-[#B45309]/50 transition-all duration-300">
                {stat.icon}
              </div>
              <span className="text-sm font-medium text-[#1C1917]/80 tracking-wide uppercase">
                {stat.label}
              </span>
            </div>)}
        </div>

        <div className="mt-20">
          <Button asChild variant="outline" size="lg" className="rounded-full border-[#B45309]/50 text-[#B45309] hover:bg-[#B45309]/10 hover:text-[#B45309] px-10" />
        </div>
      </div>
    </section>;
}